import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2, Copy, CreditCard, QrCode } from "lucide-react";
import { toast } from "sonner";
import { publicErrorMessage } from "@/lib/publicErrors";
import { PublicErrorCard } from "@/components/signing/PublicErrorCard";
import logoUrl from "@/assets/brand/simbolo.svg";
import { formatBRL } from "@/lib/format";
import SEOMeta from "@/components/SEOMeta";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const fmt = (v: number) => formatBRL(v);

interface PublicInstallment {
  id: string;
  sequence: number;
  label: string | null;
  amount: number;
  due_date: string | null;
  status: string;
  mp_status: string | null;
  payment_method: string | null;
  pix_qr_code: string | null;
  pix_qr_base64: string | null;
  budgets: { title: string } | null;
  clients: { name: string } | null;
}

async function callPublic(action: string | null, body: Record<string, unknown> = {}) {
  const url = `${SUPABASE_URL}/functions/v1/mp-public-pay`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: ANON_KEY,
      Authorization: `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify(action ? { action, ...body } : body),
  });
  if (!res.ok) throw new Error((await res.json()).error || "request_failed");
  return res.json();
}

async function getInstallment(token: string): Promise<PublicInstallment> {
  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/mp-public-pay?token=${encodeURIComponent(token)}`,
    {
      headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || "not_found");
  }
  return res.json();
}

function PayView() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const externalStatus = params.get("status");
  const [data, setData] = useState<PublicInstallment | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"" | "pix" | "card">("");
  const [method, setMethod] = useState<"pix" | "card">("pix");
  const [installments, setInstallments] = useState(12);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [lgpdConsent, setLgpdConsent] = useState(false);

  const isPaid = data?.status === "paid" || externalStatus === "success";

  const refresh = async () => {
    if (!token) return;
    try {
      setData(await getInstallment(token));
      setLoadError(null);
    } catch (e) {
      // Only the first load decides the page state; later polls just keep the last good data.
      setLoadError((prev) => prev ?? (e instanceof Error ? e.message : "not_found"));
    }
  };

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [token]);

  // Polling while waiting for PIX
  useEffect(() => {
    if (!data || data.status === "paid" || data.payment_method !== "pix" || !data.pix_qr_code)
      return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [data?.pix_qr_code, data?.status]);

  const onPix = async () => {
    if (!token) return;
    setBusy("pix");
    try {
      const r = await callPublic("pix", { token, payer_name: name, payer_email: email });
      setData((d) =>
        d
          ? { ...d, payment_method: "pix", pix_qr_code: r.qr_code, pix_qr_base64: r.qr_code_base64 }
          : d,
      );
    } catch (e) {
      toast.error(publicErrorMessage((e as Error).message));
    } finally {
      setBusy("");
    }
  };

  const onCard = async () => {
    if (!token) return;
    setBusy("card");
    try {
      const r = await callPublic("card", {
        token,
        max_installments: installments,
        return_origin: window.location.origin,
      });
      window.location.href = r.init_point;
    } catch (e) {
      toast.error(publicErrorMessage((e as Error).message));
      setBusy("");
    }
  };

  const copyPix = () => {
    if (!data?.pix_qr_code) return;
    navigator.clipboard.writeText(data.pix_qr_code);
    toast.success("Código PIX copiado");
  };

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) {
    return <PublicErrorCard code={loadError ?? "not_found"} />;
  }

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-[560px] space-y-4">
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt="Eliveuton Souza" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-semibold text-foreground">Eliveuton Souza</p>
            <p className="text-xs text-muted-foreground">Desenvolvimento Web & Automação com IA</p>
          </div>
        </div>

        <Card className="rounded-2xl border-border shadow-card">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Cobrança
                </p>
                <h1 className="mt-1 font-display text-xl font-bold tracking-tight text-foreground">
                  {data.budgets?.title || "Pagamento"}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {data.label} · parcela #{data.sequence}
                </p>
              </div>
              <Badge
                variant="outline"
                className={
                  isPaid
                    ? "border-transparent bg-ok-soft text-ok"
                    : "border-transparent bg-warn-soft text-warn"
                }
              >
                {isPaid ? "Pago" : "Pendente"}
              </Badge>
            </div>

            <div className="mt-6 flex items-end justify-between gap-4 border-t border-border pt-5">
              <div>
                <p className="text-xs text-muted-foreground">Valor</p>
                <p className="mt-1 font-display text-3xl font-bold tracking-tight text-foreground">
                  {fmt(Number(data.amount))}
                </p>
              </div>
              {data.due_date && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Vencimento</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">
                    {new Date(data.due_date).toLocaleDateString("pt-BR")}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {isPaid ? (
          <Card className="rounded-2xl border-ok bg-ok-soft shadow-card">
            <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ok text-ok-foreground">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <p className="mt-2 font-display text-lg font-bold text-foreground">
                Pagamento confirmado
              </p>
              <p className="text-sm text-muted-foreground">
                Recebemos {fmt(Number(data.amount))}. O comprovante foi enviado para o seu e-mail e
                o projeto já pode começar.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {data.pix_qr_code ? (
              <Card className="rounded-2xl border-border shadow-card">
                <CardHeader>
                  <CardTitle className="text-base font-semibold">
                    PIX gerado — escaneie para pagar
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-center">
                  {data.pix_qr_base64 && (
                    <img
                      src={`data:image/png;base64,${data.pix_qr_base64}`}
                      alt="QR Code PIX"
                      className="mx-auto h-56 w-56 rounded-xl border border-border bg-muted p-2"
                    />
                  )}
                  <Button variant="outline" className="rounded-xl" onClick={copyPix}>
                    <Copy className="mr-2 h-4 w-4" />
                    Copiar código copia-e-cola
                  </Button>
                  <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-warn" />
                    Aguardando confirmação automática…
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="rounded-2xl border-border shadow-card">
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Escolha como pagar</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div
                    className="grid gap-3 sm:grid-cols-2"
                    role="radiogroup"
                    aria-label="Forma de pagamento"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={method === "pix"}
                      onClick={() => setMethod("pix")}
                      className={`rounded-xl border p-4 text-left transition-colors ${
                        method === "pix"
                          ? "border-primary bg-accent"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                          method === "pix"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        <QrCode className="h-4 w-4" />
                      </span>
                      <p className="mt-3 text-sm font-semibold text-foreground">PIX</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        Confirmação em segundos, sem taxa
                      </p>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={method === "card"}
                      onClick={() => setMethod("card")}
                      className={`rounded-xl border p-4 text-left transition-colors ${
                        method === "card"
                          ? "border-primary bg-accent"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                          method === "card"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        <CreditCard className="h-4 w-4" />
                      </span>
                      <p className="mt-3 text-sm font-semibold text-foreground">Cartão</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        Em até 12x com juros da operadora
                      </p>
                    </button>
                  </div>

                  <div className="space-y-3.5">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="pay-nome-do-pagador"
                        className="text-xs font-semibold text-muted-foreground"
                      >
                        Nome do pagador
                      </Label>
                      <Input
                        id="pay-nome-do-pagador"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Como consta no cadastro"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="pay-e-mail-para-o-comprovante"
                        className="text-xs font-semibold text-muted-foreground"
                      >
                        E-mail para o comprovante
                      </Label>
                      <Input
                        id="pay-e-mail-para-o-comprovante"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3.5">
                    <Checkbox
                      id="lgpd-consent"
                      checked={lgpdConsent}
                      onCheckedChange={(v) => setLgpdConsent(!!v)}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor="lgpd-consent"
                      className="cursor-pointer text-xs leading-relaxed text-muted-foreground"
                    >
                      Autorizo o tratamento dos meus dados para processar este pagamento, conforme a{" "}
                      <Link
                        to="/politica-de-privacidade"
                        className="text-primary underline hover:no-underline"
                      >
                        Política de Privacidade
                      </Link>
                      .
                    </label>
                  </div>

                  {method === "pix" ? (
                    <Button
                      className="h-12 w-full rounded-xl text-base font-semibold"
                      onClick={onPix}
                      disabled={busy !== "" || !lgpdConsent}
                    >
                      {busy === "pix" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <QrCode className="mr-2 h-4 w-4" />
                      )}
                      Gerar PIX de {fmt(Number(data.amount))}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Label
                        htmlFor="pay-parcelar-em-ate"
                        className="text-xs font-semibold text-muted-foreground"
                      >
                        Parcelar em até
                      </Label>
                      <Input
                        id="pay-parcelar-em-ate"
                        type="number"
                        min={1}
                        max={12}
                        value={installments}
                        onChange={(e) => setInstallments(+e.target.value)}
                      />
                      <Button
                        className="h-12 w-full rounded-xl text-base font-semibold"
                        variant="secondary"
                        onClick={onCard}
                        disabled={busy !== "" || !lgpdConsent}
                      >
                        {busy === "card" ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="mr-2 h-4 w-4" />
                        )}
                        Pagar com cartão ({installments}x)
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Pagamentos processados pelo Mercado Pago
        </p>
      </div>
    </div>
  );
}

// Página por token: nunca indexar nem gerar preview com dado do cliente
// (docs/specs/standards/pages-and-routing.md, regra 2).
export default function Pay() {
  return (
    <>
      <SEOMeta title="Pagamento" description="Página privada de pagamento de parcela." noindex />
      <PayView />
    </>
  );
}
